import { useState, useEffect } from 'react';
import { useContext } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import './PIDashboard.scss';
import { AccessibilityContext } from '../store/accesibility-context';
import Loader from '../components/ui/Loader';
import DashboardHeader from '../components/mainDashboard/DashboardHeader';
import {
	fetchDoctors,
	fetchStudies,
	fetchStudiesByDoctor
} from '../api/studiesAPI';
import AuthContext, {
	getStoredTokenData
} from '../store/auth-context';
import WrapperModal from '../components/ui/WrapperModal';
import NewUser from '../components/forms/NewUser';
import Button from '../components/ui/Button';
import ConfirmationWarning from '../components/ui/ConfirmationWarning';
import DashboardStudyItem from '../components/mainDashboard/DashboardStudyItem';
import {
	getColorForStudyStatus,
	getColorForStudyStatus2
} from '../utils/varMappers';
import Resultspages from '../components/mainDashboard/Resultpages';
import {
	useSearchParams,
	useNavigate
} from 'react-router-dom';
import Pagination from '../components/mainDashboard/Pagination';
import { getStudiesHospital } from '../api/hospitalsAPI';

const ITEMS_PER_PAGE = 12;

const statusWithColor = {
	UNCOMPLETED: { id: 1, color: '#FF9933' },
	ONE: { id: 1, color: '#f5be50' },
	TWO: { id: 2, color: '#62c655' },
	SUBMITTED_FOR_ANALYSIS: { id: 3, color: '#74c9bb' },
	STUDY_COMPLETED: { id: 4, color: '#848484' },
	ARCHIVED: { id: 4, color: '#848484' },
	REVISION_REQUESTED: { id: 5, color: '#ee6b5f' },
	PENDING_APPROVAL: { id: 6, color: '#FF9933' }
};

const HospitalDashboard = () => {
	const accessibilityCtx = useContext(AccessibilityContext);
	const [addNewUser, setAddNewUser] = useState(false);
	const token = getStoredTokenData().token;

	const newUserHandler = () => {
		setAddNewUser(true);
	};

	const cancelStudyCreationHandler = () => {
		setAddNewUser(false);
	};
	// Integrar - hardcoded
	const [hospitalId, setHospitalId] = useState('');
	const [selectedQA, setSelectedQA] = useState('');
	const [selectedPI, setSelectedPI] = useState('');
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const queryClient = useQueryClient();
	const [userCreated, setUserCreated] = useState(false);
	const authCtx = useContext(AuthContext);

	const stateRevisionTitleQAM = {
		UNCOMPLETED: 'Missing Protocol Information',
		ONE: 'Ready For Review',
		TWO: 'Under P.I. Review',
		STUDY_COMPLETED: 'Completed',
		ARCHIVED: 'Archived',
		REVISION_REQUESTED: 'Revision Requested',
		SUBMITTED_FOR_ANALYSIS: 'Submitted For Analysis',
		PENDING_APPROVAL: 'Pending QCT Approval'
	};
	const stateRevisionTitlePI = {
		UNCOMPLETED: 'Missing Protocol Information',
		ONE: 'Under Q.A.M. Review',
		TWO: 'Ready For Review',
		STUDY_COMPLETED: 'Completed',
		ARCHIVED: 'Archived',
		REVISION_REQUESTED: 'Revision Requested',
		SUBMITTED_FOR_ANALYSIS: 'Submitted For Analysis',
		PENDING_APPROVAL: 'Pending QCT Approval'
	};

	const [currentOption, setCurrentOption] =
		useState('none');
	const [searchParams] = useSearchParams();
	const q = searchParams.get('q');

	useEffect(() => {
		if (authCtx.userData?.hospital) {
			setHospitalId(authCtx.userData.hospital);
		}
	}, [authCtx.userData.hospital]);

	const {
		data: doctorsQA,
		error: doctorsQAError,
		isLoading: loadingDoctorsQA
	} = useQuery({
		queryKey: ['doctors', hospitalId, 'qa'],
		queryFn: () => fetchDoctors(hospitalId, 'qa', token),
		retry: false,
		staleTime: 3000000
	});

	const {
		data: doctorsPI,
		error: doctorsPIError,
		isLoading: loadingDoctorsPI
	} = useQuery({
		queryKey: ['doctors', hospitalId, 'pi'],
		queryFn: () => fetchDoctors(hospitalId, 'pi', token),
		retry: false,
		staleTime: 3000000
	});

	const selectedDoctorId = selectedQA || selectedPI;
	const doctorId = selectedDoctorId;
	const role = selectedQA ? 'qa' : 'pi';

	const studiesPagesQuery = useQuery({
		queryKey: ['studies-hospital', role, doctorId, page],
		queryFn: () =>
			getStudiesHospital({ role, doctorId, page }),
		retry: false,
		staleTime: 3000000 //5 minutes to refetch
	});

	const {
		data: studiesData,
		error: studiesError,
		isLoading: loadingStudies
	} = useQuery({
		queryKey: ['studies', role, selectedDoctorId, page],
		queryFn: () =>
			fetchStudiesByDoctor(
				role,
				selectedDoctorId,
				page,
				token
			),
		retry: false,
		staleTime: 3000000,
		enabled: !!selectedDoctorId
	});

	const handleQAChange = (event) => {
		setSelectedQA(event.target.value);
		setSelectedPI('');
		setPage(1);
		queryClient.invalidateQueries('studies');
	};

	const handlePIChange = (event) => {
		setSelectedPI(event.target.value);
		setSelectedQA('');
		setPage(1);
		queryClient.invalidateQueries('studies');
	};

	const handlePageChange = (newPage) => {
		setPage(newPage);
		queryClient.invalidateQueries('studies');
	};

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
		setPage(1); // Reset page to 1 when search term changes
	};

	const filteredStudies = studiesData
		? studiesData.filter((study) =>
				study.title
					.toLowerCase()
					.includes(searchTerm.toLowerCase())
			)
		: [];

	const totalPages = Math.ceil(
		filteredStudies.length / ITEMS_PER_PAGE
	);
	const paginatedStudies = filteredStudies.slice(
		(page - 1) * ITEMS_PER_PAGE,
		page * ITEMS_PER_PAGE
	);
	if (
		loadingDoctorsQA ||
		loadingDoctorsPI ||
		loadingStudies ||
		studiesPagesQuery.isLoading
	) {
		return <Loader />;
	}

	if (doctorsQAError) {
		return (
			<div>
				Error loading QA doctors: {doctorsQAError.message}
			</div>
		);
	}

	if (doctorsPIError) {
		return (
			<div>
				Error loading PI doctors: {doctorsPIError.message}
			</div>
		);
	}

	if (studiesError) {
		return (
			<div>
				Error loading studies: {studiesError.message}
			</div>
		);
	}

	return (
		<div className='dashboard-general-wrapper'>
			<DashboardHeader />
			<div className='dashboard-content-container'>
				<div className='content-header'>
					<div className='content-title'>
						<h2>Coverage Analysis</h2>
					</div>
					<div className='content-controls'>
						<div>
							<select
								className='form-control study-state-selector'
								value={selectedQA}
								onChange={handleQAChange}
							>
								<option value=''>Filter by Q.A</option>
								{doctorsQA &&
									doctorsQA.map((doc) => (
										<option value={doc._id} key={doc._id}>
											{`${doc.firstname} ${doc.lastname}`}
										</option>
									))}
							</select>
						</div>
						<div>
							<select
								className='form-control study-state-selector'
								value={selectedPI}
								onChange={handlePIChange}
							>
								<option value=''>Filter by P.I</option>
								{doctorsPI &&
									doctorsPI.map((doc) => (
										<option value={doc._id} key={doc._id}>
											{`${doc.firstname} ${doc.lastname}`}
										</option>
									))}
							</select>
						</div>
					</div>
				</div>

				<div className='studies-list-container'>
					{loadingStudies && <Loader />}
					{paginatedStudies &&
						paginatedStudies.map((study) => (
							<DashboardStudyItem
								key={`${study._id}${
									authCtx.userData.role === 'QA'
										? (study.pi?.id ?? 'N/A')
										: (study.qam?.id ?? 'N/A')
								}`}
								id={study._id}
								// TODO color should come from the backend as a string
								color={
									accessibilityCtx.contrast === false
										? getColorForStudyStatus(study.status)
										: getColorForStudyStatus2(study.status)
								}
								title={study.title}
								status={
									authCtx.userData.role === 'QA'
										? stateRevisionTitleQAM[study.status]
										: stateRevisionTitlePI[study.status]
								}
								type={study.type}
								sponsor={study.sponsor}
								role={
									authCtx.userData.role === 'QA'
										? 'P.I.'
										: 'Q.A.'
								}
								username={
									authCtx.userData.role === 'QA'
										? (study.pi?.firstname ?? 'N/A')
										: (study.qam?.firstname ?? 'N/A')
								}
								docUpload={study.documentsUploaded}
								warning=''
							/>
						))}
				</div>
				<div className='pagination'>
					<Pagination
						currentPage={page}
						sizeNumber={ITEMS_PER_PAGE}
						totalNumber={filteredStudies.length}
						changePage={handlePageChange}
					/>
				</div>
			</div>
		</div>
	);
};

export default HospitalDashboard;
